/**
 * @fileoverview
 * Sidebar Component - Application sidebar navigation with menu items and submenus
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  computed,
  signal,
} from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AuthService, UserType } from '@core/authentication';
import {
  USER_PERMISSIONS,
  ROLE_PERMISSIONS,
  PERMISSION_PERMISSIONS,
  DEPARTMENT_PERMISSIONS,
  AUDIT_TYPE_PERMISSIONS,
  GENERIC_PERMISSIONS,
  AUDIT_SCHEDULE_PERMISSIONS,
  DYNAMIC_FORM_PERMISSIONS,
  CASH_AUDIT_PERMISSIONS,
  REGISTER_STATUTORY_PERMISSIONS,
  GOLD_AUDIT_PERMISSIONS,
  GOLD_VALUATION_TOOL_PERMISSIONS,
  GOLD_VERIFICATION_PERMISSIONS,
  PACKET_INVENTORY_PERMISSIONS,
  SURVEILLANCE_PERMISSIONS,
  ROUTINE_CHECKING_PERMISSIONS,
  LOAN_PROCESS_CHECKING_PERMISSIONS,
  TARE_WEIGHT_PERMISSIONS,
  ONLINE_PERMISSIONS,
  KYC_DISBURSEMENT_PERMISSIONS,
  RELEASE_PERMISSIONS,
  QUERY_MANAGEMENT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  ASSET_MANAGEMENT_PERMISSIONS,
  ASSET_ENTRY_PERMISSIONS,
  ASSET_AUDIT_PERMISSIONS,
  VAULT_KEY_ENTRY_PERMISSIONS,
  KEY_MOVEMENT_SENDER_PERMISSIONS,
  KEY_MOVEMENT_RECEIVER_PERMISSIONS,
  KEY_AUDIT_PERMISSIONS,
  BRANCH_AUDIT_PERMISSIONS,
  SECURITY_PERMISSIONS,
  SCORECARD_PERMISSIONS,
  SCORECARD_PARAMETER_PERMISSIONS,
} from '@core/constants';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() collapsed: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() sidebarOpen: boolean = false;
  @Output() toggleSidebar = new EventEmitter<boolean>();
  @Output() mobileSidebarClose = new EventEmitter<void>();
  @Output() activeMenuTitle = new EventEmitter<string>();

  private routerSubscription: Subscription = new Subscription();
  private authService = inject(AuthService);
  expandedMenu: string | null = null;
  hoveredMenu: string | null = null;
  hoveredRegularMenu: string | null = null;
  private menuItemPositions: Map<string, { top: number; left: number }> =
    new Map();
  private regularMenuItemPositions: Map<string, { top: number; left: number }> =
    new Map();
  private hideTimeout: any = null;
  private regularMenuHideTimeout: any = null;

  // Signal to trigger computed signal recalculation when active state changes
  private activeStateTrigger = signal(0);

  // All menu items with their required permissions
  private allMenuItems = [
    {
      icon: 'home',
      label: 'Dashboard',
      route: '/dashboard',
      active: false,
      permission: null, // Dashboard accessible to all authenticated users
      svgPath:
        'M10.5 8.75V6.75C10.5 5.10626 10.5 4.28439 10.046 3.73121C9.96291 3.62995 9.87005 3.53709 9.76879 3.45398C9.21561 3 8.39374 3 6.75 3C5.10626 3 4.28439 3 3.73121 3.45398C3.62995 3.53709 3.53709 3.62995 3.45398 3.73121C3 4.28439 3 5.10626 3 6.75V8.75C3 10.3937 3 11.2156 3.45398 11.7688C3.53709 11.8701 3.62995 11.9629 3.73121 12.046C4.28439 12.5 5.10626 12.5 6.75 12.5C8.39374 12.5 9.21561 12.5 9.76879 12.046C9.87005 11.9629 9.96291 11.8701 10.046 11.7688C10.5 11.2156 10.5 10.3937 10.5 8.75Z M7.75 15.5H5.75C5.05222 15.5 4.70333 15.5 4.41943 15.5861C3.78023 15.78 3.28002 16.2802 3.08612 16.9194C3 17.2033 3 17.5522 3 18.25C3 18.9478 3 19.2967 3.08612 19.5806C3.28002 20.2198 3.78023 20.72 4.41943 20.9139C4.70333 21 5.05222 21 5.75 21H7.75C8.44778 21 8.79667 21 9.08057 20.9139C9.71977 20.72 10.22 20.2198 10.4139 19.5806C10.5 19.2967 10.5 18.9478 10.5 18.25C10.5 17.5522 10.5 17.2033 10.4139 16.9194C10.22 16.2802 9.71977 15.78 9.08057 15.5861C8.79667 15.5 8.44778 15.5 7.75 15.5Z M21 17.25V15.25C21 13.6063 21 12.7844 20.546 12.2312C20.4629 12.1299 20.3701 12.0371 20.2688 11.954C19.7156 11.5 18.8937 11.5 17.25 11.5C15.6063 11.5 14.7844 11.5 14.2312 11.954C14.1299 12.0371 14.0371 12.1299 13.954 12.2312C13.5 12.7844 13.5 13.6063 13.5 15.25V17.25C13.5 18.8937 13.5 19.7156 13.954 20.2688C14.0371 20.3701 14.1299 20.4629 14.2312 20.546C14.7844 21 15.6063 21 17.25 21C18.8937 21 19.7156 21 20.2688 20.546C20.3701 20.4629 20.4629 20.3701 20.546 20.2688C21 19.7156 21 18.8937 21 17.25Z M18.25 3H16.25C15.5522 3 15.2033 3 14.9194 3.08612C14.2802 3.28002 13.78 3.78023 13.5861 4.41943C13.5 4.70333 13.5 5.05222 13.5 5.75C13.5 6.44778 13.5 6.79667 13.5861 7.08057C13.78 7.71977 14.2802 8.21998 14.9194 8.41388C15.2033 8.5 15.5522 8.5 16.25 8.5H18.25C18.9478 8.5 19.2967 8.5 19.5806 8.41388C20.2198 8.21998 20.72 7.71977 20.9139 7.08057C21 6.79667 21 6.44778 21 5.75C21 5.05222 21 4.70333 20.9139 4.41943C20.72 3.78023 20.2198 3.28002 19.5806 3.08612C19.2967 3 18.9478 3 18.25 3Z',
    },
    {
      icon: 'users',
      label: 'Cash',
      route: '/cash',
      active: false,
      permission: CASH_AUDIT_PERMISSIONS.READ,
      svgPath:
        'M20.0007 9.90909V7.81818C20.0007 6.12494 20.0007 5.27832 19.7327 4.60214C19.3019 3.5151 18.3909 2.65765 17.2359 2.2522C16.5174 2 15.6179 2 13.8188 2C10.6705 2 9.09624 2 7.83897 2.44135C5.81774 3.15088 4.22342 4.65142 3.46954 6.55375C3.00061 7.73706 3.00061 9.21865 3.00061 12.1818V14.7273C3.00061 17.7966 3.00061 19.3313 3.84831 20.3971C4.09119 20.7025 4.37923 20.9736 4.70368 21.2022C5.83607 22 7.46667 22 10.7279 22H11.5007C12.0353 22 13.0344 22 13.5007 21.9972M3.00061 12C3.00061 10.159 4.49299 8.66667 6.33394 8.66667C6.99972 8.66667 7.78464 8.78333 8.43197 8.60988C9.00712 8.45576 9.45637 8.00652 9.61048 7.43136C9.78393 6.78404 9.66727 5.99912 9.66727 5.33333C9.66727 3.49238 11.1597 2 13.0007 2 M20.7527 15.8111C20.8576 14.5466 18.9229 13.5141 17.4443 14.2069C15.5973 15.0722 15.7584 17.2594 18.0395 17.3751C19.0546 17.4265 19.9425 17.3169 20.5459 17.9708C21.1493 18.6248 21.4108 20.2901 19.6322 20.8553C17.8535 21.4204 15.9996 20.4125 15.9996 19.1831M18.4707 13V13.9777M18.4707 21.2204V22',
    },
    {
      icon: 'user',
      label: 'Gold',
      route: null,
      active: false,
      permission: GOLD_AUDIT_PERMISSIONS.READ,
      hasSubmenu: true,
      submenus: [
        {
          label: 'Valuation Tool',
          route: '/gold/valuation-tool',
          active: false,
          permission: GOLD_VALUATION_TOOL_PERMISSIONS.READ,
        },
        {
          label: 'Gold Verification',
          route: '/gold/gold-verification',
          active: false,
          permission: GOLD_VERIFICATION_PERMISSIONS.READ,
        },
      ],
      svgPath:
        'M14.2644 18.3717C14.5806 17.407 14.7387 16.9247 15.0616 16.593C15.215 16.4354 15.3932 16.3048 15.5887 16.2066C16.0003 16 16.5003 16 17.5002 16C18.5001 16 19.0001 16 19.4116 16.2066C19.6072 16.3048 19.7853 16.4354 19.9388 16.593C20.2617 16.9247 20.4198 17.407 20.736 18.3717L21.0624 19.3675C21.4561 20.5688 21.653 21.1694 21.3587 21.5847C21.0643 22 20.4418 22 19.1966 22H15.8038C14.5586 22 13.936 22 13.6417 21.5847C13.3473 21.1694 13.5442 20.5688 13.938 19.3675L14.2644 18.3717Z M8.76421 10.3717C9.08041 9.40705 9.23851 8.92471 9.56143 8.59298C9.71485 8.43537 9.89299 8.3048 10.0885 8.20663C10.5001 8 11.0001 8 12 8C12.9999 8 13.4999 8 13.9115 8.20663C14.107 8.3048 14.2851 8.43537 14.4386 8.59298C14.7615 8.92471 14.9196 9.40705 15.2358 10.3717L15.5622 11.3675C15.956 12.5688 16.1528 13.1694 15.8585 13.5847C15.5642 14 14.9416 14 13.6964 14H10.3036C9.05842 14 8.43585 14 8.1415 13.5847C7.84716 13.1694 8.04404 12.5688 8.43779 11.3675L8.76421 10.3717Z M3.26439 18.3717C3.58059 17.407 3.73869 16.9247 4.06162 16.593C4.21504 16.4354 4.39317 16.3048 4.58872 16.2066C5.00031 16 5.50027 16 6.50018 16C7.5001 16 8.00006 16 8.41165 16.2066C8.6072 16.3048 8.78533 16.4354 8.93875 16.593C9.26167 16.9247 9.41977 17.407 9.73597 18.3717L10.0624 19.3675C10.4561 20.5688 10.653 21.1694 10.3587 21.5847C10.0643 22 9.44176 22 8.19661 22H4.80375C3.5586 22 2.93603 22 2.64169 21.5847C2.34735 21.1694 2.54422 20.5688 2.93797 19.3675L3.26439 18.3717Z M12 2V4M7.5 3.5L9 5M16.5 3.5L15 5',
    },
    {
      icon: 'check-square',
      label: 'Packet Inventory',
      route: '/packet-inventory',
      active: false,
      permission: PACKET_INVENTORY_PERMISSIONS.READ,
      svgPath:
        'M11 22C10.1818 22 9.40019 21.6698 7.83693 21.0095C3.94564 19.3657 2 18.5438 2 17.1613C2 16.7742 2 10.0645 2 7M11 22V11.3548M11 22C11.3404 22 11.6463 21.9428 12 21.8285M20 7V11.5 M7.32592 9.69138L4.40472 8.27785C2.80157 7.5021 2 7.11423 2 6.5C2 5.88577 2.80157 5.4979 4.40472 4.72215L7.32592 3.30862C9.12883 2.43621 10.0303 2 11 2C11.9697 2 12.8712 2.4362 14.6741 3.30862L17.5953 4.72215C19.1984 5.4979 20 5.88577 20 6.5C20 7.11423 19.1984 7.5021 17.5953 8.27785L14.6741 9.69138C12.8712 10.5638 11.9697 11 11 11C10.0303 11 9.12883 10.5638 7.32592 9.69138Z M5 12L7 13 M16 4L6 9 M20.1322 20.1589L22 22M21.2074 17.5964C21.2074 19.5826 19.594 21.1928 17.6037 21.1928C15.6134 21.1928 14 19.5826 14 17.5964C14 15.6102 15.6134 14 17.6037 14C19.594 14 21.2074 15.6102 21.2074 17.5964Z',
    },
    {
      icon: 'settings',
      label: 'Surveillance',
      route: null,
      active: false,
      permission: SURVEILLANCE_PERMISSIONS.READ,
      hasSubmenu: true,
      submenus: [
        {
          label: 'Routine Checking',
          route: '/surveillance/routine-checking',
          active: false,
          permission: ROUTINE_CHECKING_PERMISSIONS.READ,
        },
        {
          label: 'Loan Process Checking',
          route: '/surveillance/loan-process-checking',
          active: false,
          permission: LOAN_PROCESS_CHECKING_PERMISSIONS.READ,
        },
      ],
      svgPath:
        'M15 13V14C15 16.8284 15 18.2426 15.8787 19.1213C16.7574 20 18.1716 20 21 20 M20.6914 18L20.6914 22 M9 10.8292L8.46281 10.8889C7.65914 10.9782 7.25729 11.0229 6.93718 10.9882C5.37478 10.8189 4.1433 9.43436 4.00816 7.69526C3.98047 7.33895 4.02507 6.89263 4.11426 6 M13.6914 7H13.7004 M17 7L21.5 7 M2.246 4.22415C2.02398 3.32732 1.91297 2.8789 2.08182 2.5451C2.13924 2.43157 2.21764 2.33017 2.31277 2.24638C2.59249 2 3.05019 2 3.9656 2H16.5537C19.5616 2 22 4.46243 22 7.5C22 10.5376 19.5616 13 16.5537 13H11.8051C10.9893 13 10.5813 13 10.2309 12.8603C9.96064 12.7526 9.71719 12.5861 9.51778 12.3727C9.25919 12.096 9.10768 11.7135 8.80467 10.9485L8.26723 9.59167C7.95643 8.80701 7.80103 8.41468 7.51359 8.1244C7.22614 7.83413 6.83765 7.67719 6.06065 7.36333L4.3686 6.67983C3.64185 6.38626 3.27848 6.23948 3.00574 5.98088C2.91013 5.89022 2.82367 5.79019 2.74764 5.68227C2.53076 5.37443 2.43584 4.99101 2.246 4.22415Z',
    },
    {
      icon: 'trending-up',
      label: 'Tare Weight',
      route: '/tare-weight',
      active: false,
      permission: TARE_WEIGHT_PERMISSIONS.READ,
      svgPath:
        'M12 5V22M12 22H9M12 22H15 M21 13L18.5 8L16 13 M8 13L5.5 8L3 13 M4 8H5.0482C6.31166 8 7.5375 7.471 8.5241 6.5C10.5562 4.5 13.4438 4.5 15.4759 6.5C16.4625 7.471 17.6883 8 18.9518 8H20 M18.5 17C19.8547 17 21.0344 16.1663 21.6473 14.9349C21.978 14.2702 22.1434 13.9379 21.8415 13.469C21.5396 13 21.04 13 20.0407 13H16.9593C15.96 13 15.4604 13 15.1585 13.469C14.8566 13.9379 15.022 14.2702 15.3527 14.9349C15.9656 16.1663 17.1453 17 18.5 17Z M5.5 17C6.85471 17 8.03442 16.1663 8.64726 14.9349C8.97802 14.2702 9.14339 13.9379 8.84151 13.469C8.53962 13 8.04 13 7.04075 13H3.95925C2.96 13 2.46038 13 2.15849 13.469C1.85661 13.9379 2.02198 14.2702 2.35273 14.9349C2.96558 16.1663 4.14528 17 5.5 17Z M13.5 3.5C13.5 4.32843 12.8284 5 12 5C11.1716 5 10.5 4.32843 10.5 3.5C10.5 2.67157 11.1716 2 12 2C12.8284 2 13.5 2.67157 13.5 3.5Z',
    },
    {
      icon: 'check-square',
      label: 'Registers & Statutory',
      route: '/registers-and-statutory',
      active: false,
      permission: REGISTER_STATUTORY_PERMISSIONS.READ,
      svgPaths: [
        'M20 13V10.6569C20 9.83935 20 9.4306 19.8478 9.06306C19.6955 8.69552 19.4065 8.40649 18.8284 7.82843L14.0919 3.09188C13.593 2.593 13.3436 2.34355 13.0345 2.19575C12.9702 2.165 12.9044 2.13772 12.8372 2.11401C12.5141 2 12.1614 2 11.4558 2C8.21082 2 6.58831 2 5.48933 2.88607C5.26731 3.06508 5.06508 3.26731 4.88607 3.48933C4 4.58831 4 6.21082 4 9.45584V14C4 17.7712 4 19.6569 5.17157 20.8284C6.23467 21.8915 7.8857 21.99 11 21.9991M13 2.5V3C13 5.82843 13 7.24264 13.8787 8.12132C14.7574 9 16.1716 9 19 9H19.5',
        'M20 22L17.8529 19.8529M17.8529 19.8529C17.9675 19.7384 18.0739 19.6158 18.1714 19.486C18.602 18.913 18.8571 18.2006 18.8571 17.4286C18.8571 15.535 17.3221 14 15.4286 14C13.535 14 12 15.535 12 17.4286C12 19.3221 13.535 20.8571 15.4286 20.8571C16.3753 20.8571 17.2325 20.4734 17.8529 19.8529Z',
      ],
    },
    {
      icon: 'online',
      label: 'Online',
      route: null,
      active: false,
      permission: ONLINE_PERMISSIONS.READ,
      hasSubmenu: true,
      submenus: [
        {
          label: 'KYC and Disbursement',
          route: '/online/kyc-and-disbursement',
          active: false,
          permission: KYC_DISBURSEMENT_PERMISSIONS.READ,
        },
        {
          label: 'Release',
          route: '/online/release',
          active: false,
          permission: RELEASE_PERMISSIONS.READ,
        },
      ],
      svgPaths: [
        'M20.9992 10C20.9907 6.41543 20.8861 4.52814 19.6088 3.31802C18.2175 2 15.9783 2 11.5 2C7.02166 2 4.78249 2 3.39124 3.31802C2 4.63604 2 6.75736 2 11C2 15.2426 2 17.364 3.39124 18.682C4.61763 19.8438 6.50289 19.9815 10 19.9978',
        'M15 17.5C15 17.5 15.5 17.5 16 18.5C16 18.5 17.5882 16 19 15.5',
        'M22 17C22 19.7614 19.7614 22 17 22C14.2386 22 12 19.7614 12 17C12 14.2386 14.2386 12 17 12C19.7614 12 22 14.2386 22 17Z',
        'M2 8.5H21',
        'M6.49981 5.5H6.50879',
        'M10.4998 5.5H10.5088',
      ],
    },

    {
      icon: 'masters',
      label: 'Masters',
      route: null,
      active: false,
      permission: null, // Show if user has any masters permission
      svgPaths: [
        'M21.3175 7.14139L20.8239 6.28479C20.4506 5.63696 20.264 5.31305 19.9464 5.18388C19.6288 5.05472 19.2696 5.15664 18.5513 5.36048L17.3311 5.70418C16.8725 5.80994 16.3913 5.74994 15.9726 5.53479L15.6357 5.34042C15.2766 5.11043 15.0004 4.77133 14.8475 4.37274L14.5136 3.37536C14.294 2.71534 14.1842 2.38533 13.9228 2.19657C13.6615 2.00781 13.3143 2.00781 12.6199 2.00781H11.5051C10.8108 2.00781 10.4636 2.00781 10.2022 2.19657C9.94085 2.38533 9.83106 2.71534 9.61149 3.37536L9.27753 4.37274C9.12465 4.77133 8.84845 5.11043 8.48937 5.34042L8.15249 5.53479C7.73374 5.74994 7.25259 5.80994 6.79398 5.70418L5.57375 5.36048C4.85541 5.15664 4.49625 5.05472 4.17867 5.18388C3.86109 5.31305 3.67445 5.63696 3.30115 6.28479L2.80757 7.14139C2.45766 7.74864 2.2827 8.05227 2.31666 8.37549C2.35061 8.69871 2.58483 8.95918 3.05326 9.48012L4.0843 10.6328C4.3363 10.9518 4.51521 11.5078 4.51521 12.0077C4.51521 12.5078 4.33636 13.0636 4.08433 13.3827L3.05326 14.5354C2.58483 15.0564 2.35062 15.3168 2.31666 15.6401C2.2827 15.9633 2.45766 16.2669 2.80757 16.8741L3.30114 17.7307C3.67443 18.3785 3.86109 18.7025 4.17867 18.8316C4.49625 18.9608 4.85542 18.8589 5.57377 18.655L6.79394 18.3113C7.25263 18.2055 7.73387 18.2656 8.15267 18.4808L8.4895 18.6752C8.84851 18.9052 9.12464 19.2442 9.2775 19.6428L9.61149 20.6403C9.83106 21.3003 9.94085 21.6303 10.2022 21.8191C10.4636 22.0078 10.8108 22.0078 11.5051 22.0078H12.6199C13.3143 22.0078 13.6615 22.0078 13.9228 21.8191C14.1842 21.6303 14.294 21.3003 14.5136 20.6403L14.8476 19.6428C15.0004 19.2442 15.2765 18.9052 15.6356 18.6752L15.9724 18.4808C16.3912 18.2656 16.8724 18.2055 17.3311 18.3113L18.5513 18.655C19.2696 18.8589 19.6288 18.9608 19.9464 18.8316C20.264 18.7025 20.4506 18.3785 20.8239 17.7307L21.3175 16.8741C21.6674 16.2669 21.8423 15.9633 21.8084 15.6401C21.7744 15.3168 21.5402 15.0564 21.0718 14.5354L20.0407 13.3827C19.7887 13.0636 19.6098 12.5078 19.6098 12.0077C19.6098 11.5078 19.7888 10.9518 20.0407 10.6328L21.0718 9.48012C21.5402 8.95918 21.7744 8.69871 21.8084 8.37549C21.8423 8.05227 21.6674 7.74864 21.3175 7.14139Z',
        'M8.49997 16C9.1986 14.7923 10.5044 13.9797 12 13.9797C13.4955 13.9797 14.8013 14.7923 15.5 16M14 9.50002C14 10.6046 13.1046 11.5 12 11.5C10.8954 11.5 10 10.6046 10 9.50002C10 8.39545 10.8954 7.50002 12 7.50002C13.1046 7.50002 14 8.39545 14 9.50002Z',
      ],
      hasSubmenu: true,
      submenus: [
        {
          label: 'Audit Type Master',
          route: '/masters/audit-type',
          active: false,
          permission: AUDIT_TYPE_PERMISSIONS.READ,
        },
        {
          label: 'Generic Master',
          route: '/masters/generic',
          active: false,
          permission: GENERIC_PERMISSIONS.READ,
        },
        {
          label: 'User Master',
          route: '/masters/user-master',
          active: false,
          permission: USER_PERMISSIONS.READ,
        },
        {
          label: 'Department Master',
          route: '/masters/department-master',
          active: false,
          permission: DEPARTMENT_PERMISSIONS.READ,
        },
        {
          label: 'Role Master',
          route: '/masters/role-master',
          active: false,
          permission: ROLE_PERMISSIONS.READ,
        },
        {
          label: 'Permission Master',
          route: '/masters/permission',
          active: false,
          permission: PERMISSION_PERMISSIONS.READ,
        },
      ],
    },
    {
      icon: 'settings',
      label: 'Settings',
      route: null,
      active: false,
      permission: null, // Show if user has any settings permission
      hasSubmenu: true,
      submenus: [
        {
          label: 'Audit Schedule',
          route: '/settings/audit-schedule',
          active: false,
          permission: AUDIT_SCHEDULE_PERMISSIONS.READ,
        },
        {
          label: 'Dynamic Form',
          route: '/settings/dynamic-form',
          active: false,
          permission: DYNAMIC_FORM_PERMISSIONS.READ,
        },
        {
          label: 'Database Integration',
          route: '/settings/database-integration',
          active: false,
          permission: null, // TODO: Add DATABASE_INTEGRATION_PERMISSIONS when defined
        },
      ],
      svgPaths: [
        'M21.3175 7.14139L20.8239 6.28479C20.4506 5.63696 20.264 5.31305 19.9464 5.18388C19.6288 5.05472 19.2696 5.15664 18.5513 5.36048L17.3311 5.70418C16.8725 5.80994 16.3913 5.74994 15.9726 5.53479L15.6357 5.34042C15.2766 5.11043 15.0004 4.77133 14.8475 4.37274L14.5136 3.37536C14.294 2.71534 14.1842 2.38533 13.9228 2.19657C13.6615 2.00781 13.3143 2.00781 12.6199 2.00781H11.5051C10.8108 2.00781 10.4636 2.00781 10.2022 2.19657C9.94085 2.38533 9.83106 2.71534 9.61149 3.37536L9.27753 4.37274C9.12465 4.77133 8.84845 5.11043 8.48937 5.34042L8.15249 5.53479C7.73374 5.74994 7.25259 5.80994 6.79398 5.70418L5.57375 5.36048C4.85541 5.15664 4.49625 5.05472 4.17867 5.18388C3.86109 5.31305 3.67445 5.63696 3.30115 6.28479L2.80757 7.14139C2.45766 7.74864 2.2827 8.05227 2.31666 8.37549C2.35061 8.69871 2.58483 8.95918 3.05326 9.48012L4.0843 10.6328C4.3363 10.9518 4.51521 11.5078 4.51521 12.0077C4.51521 12.5078 4.33636 13.0636 4.08433 13.3827L3.05326 14.5354C2.58483 15.0564 2.35062 15.3168 2.31666 15.6401C2.2827 15.9633 2.45766 16.2669 2.80757 16.8741L3.30114 17.7307C3.67443 18.3785 3.86109 18.7025 4.17867 18.8316C4.49625 18.9608 4.85542 18.8589 5.57377 18.655L6.79394 18.3113C7.25263 18.2055 7.73387 18.2656 8.15267 18.4808L8.4895 18.6752C8.84851 18.9052 9.12464 19.2442 9.2775 19.6428L9.61149 20.6403C9.83106 21.3003 9.94085 21.6303 10.2022 21.8191C10.4636 22.0078 10.8108 22.0078 11.5051 22.0078H12.6199C13.3143 22.0078 13.6615 22.0078 13.9228 21.8191C14.1842 21.6303 14.294 21.3003 14.5136 20.6403L14.8476 19.6428C15.0004 19.2442 15.2765 18.9052 15.6356 18.6752L15.9724 18.4808C16.3912 18.2656 16.8724 18.2055 17.3311 18.3113L18.5513 18.655C19.2696 18.8589 19.6288 18.9608 19.9464 18.8316C20.264 18.7025 20.4506 18.3785 20.8239 17.7307L21.3175 16.8741C21.6674 16.2669 21.8423 15.9633 21.8084 15.6401C21.7744 15.3168 21.5402 15.0564 21.0718 14.5354L20.0407 13.3827C19.7887 13.0636 19.6098 12.5078 19.6098 12.0077C19.6098 11.5078 19.7888 10.9518 20.0407 10.6328L21.0718 9.48012C21.5402 8.95918 21.7744 8.69871 21.8084 8.37549C21.8423 8.05227 21.6674 7.74864 21.3175 7.14139Z',
        'M15.5195 12C15.5195 13.933 13.9525 15.5 12.0195 15.5C10.0865 15.5 8.51953 13.933 8.51953 12C8.51953 10.067 10.0865 8.5 12.0195 8.5C13.9525 8.5 15.5195 10.067 15.5195 12Z',
      ],
    },
    {
      icon: 'building',
      label: 'Branch Audit',
      route: '/branch-audit',
      active: false,
      permission: BRANCH_AUDIT_PERMISSIONS.READ,
      svgPath:
        'M3 21H21M4 21V10L12 3L20 10V21M7 21V13H9V21M11 21V13H13V21M15 21V13H17V21',
    },
    {
      icon: 'query-management',
      label: 'Query Management',
      route: '/query-management',
      active: false,
      permission: QUERY_MANAGEMENT_PERMISSIONS.READ,
      svgPaths: [
        'M12 3.00366C11.4383 3.01203 10.3789 3.03449 9.8294 3.07102C5.64639 3.34908 2.31441 6.72832 2.04024 10.9707C1.98659 11.8009 1.98659 12.6606 2.04024 13.4908C2.1401 15.0359 2.82343 16.4665 3.62791 17.6746C4.09501 18.5203 3.78674 19.5758 3.30021 20.4978C2.94941 21.1625 2.77401 21.4949 2.91484 21.735C3.05568 21.9752 3.37026 21.9828 3.99943 21.9981C5.24367 22.0284 6.08268 21.6757 6.74868 21.1846C7.1264 20.906 7.31527 20.7668 7.44544 20.7508C7.5756 20.7347 7.83177 20.8403 8.34401 21.0512C8.8044 21.2408 9.33896 21.3579 9.8294 21.3905C11.2536 21.4851 12.7435 21.4853 14.1706 21.3905C18.3536 21.1124 21.6856 17.7332 21.9598 13.4908C21.9915 13.0001 22.0044 12.4991 21.9987 11.9999',
        'M8.5 15H15.5M8.5 10H12',
        'M20.2649 7.27042L22 9M21.1714 5.08571C21.1714 3.38152 19.7899 2 18.0857 2C16.3815 2 15 3.38152 15 5.08571C15 6.78991 16.3815 8.17143 18.0857 8.17143C19.7899 8.17143 21.1714 6.78991 21.1714 5.08571Z',
      ],
    },
    {
      icon: 'reports',
      label: 'Reports',
      route: '/reports',
      active: false,
      permission: REPORTS_PERMISSIONS.READ,
      svgPaths: [
        'M7 17L7 13',
        'M12 17L12 7',
        'M17 17L17 11',
        'M2.5 12C2.5 7.52166 2.5 5.28249 3.89124 3.89124C5.28249 2.5 7.52166 2.5 12 2.5C16.4783 2.5 18.7175 2.5 20.1088 3.89124C21.5 5.28249 21.5 7.52166 21.5 12C21.5 16.4783 21.5 18.7175 20.1088 20.1088C18.7175 21.5 16.4783 21.5 12 21.5C7.52166 21.5 5.28249 21.5 3.89124 20.1088C2.5 18.7175 2.5 16.4783 2.5 12Z',
      ],
    },
    {
      icon: 'security',
      label: 'Security',
      route: '/security',
      active: false,
      permission: SECURITY_PERMISSIONS.READ,
      svgPaths: [
        'M18.7088 3.49534C16.8165 2.55382 14.5009 2 12 2C9.4991 2 7.1835 2.55382 5.29116 3.49534C4.36318 3.95706 3.89919 4.18792 3.4496 4.91378C3 5.63965 3 6.34248 3 7.74814V11.2371C3 16.9205 7.54236 20.0804 10.173 21.4338C10.9067 21.8113 11.2735 22 12 22C12.7265 22 13.0933 21.8113 13.8269 21.4338C16.4576 20.0804 21 16.9205 21 11.2371L21 7.74814C21 6.34249 21 5.63966 20.5504 4.91378C20.1008 4.18791 19.6368 3.95706 18.7088 3.49534Z',
        'M10 10V8.5C10 7.39543 10.8954 6.5 12 6.5C13.1046 6.5 14 7.39543 14 8.5V10',
        'M14 10H10C9.17157 10 8.5 10.6716 8.5 11.5V13C8.5 13.8284 9.17157 14.5 10 14.5H14C14.8284 14.5 15.5 13.8284 15.5 13V11.5C15.5 10.6716 14.8284 10 14 10Z',
      ],
    },
    {
      icon: 'purse',
      label: 'Asset Management',
      route: null,
      active: false,
      permission: ASSET_MANAGEMENT_PERMISSIONS.READ,
      hasSubmenu: true,
      submenus: [
        {
          label: 'Asset Entry',
          route: '/asset-management/asset-entry',
          active: false,
          permission: ASSET_ENTRY_PERMISSIONS.READ,
        },
        {
          label: 'Asset Audit',
          route: '/asset-management/asset-audit',
          active: false,
          permission: ASSET_AUDIT_PERMISSIONS.READ,
        },
        {
          label: 'Vault & Key Entry',
          route: '/asset-management/vault-key-entry',
          active: false,
          permission: VAULT_KEY_ENTRY_PERMISSIONS.READ,
        },
        {
          label: 'Key Movement Sender',
          route: '/asset-management/key-movement-sender',
          active: false,
          permission: KEY_MOVEMENT_SENDER_PERMISSIONS.READ,
        },
        {
          label: 'Key Movement Receiver',
          route: '/asset-management/key-movement-receiver',
          active: false,
          permission: KEY_MOVEMENT_RECEIVER_PERMISSIONS.READ,
        },
        {
          label: 'Key Audit',
          route: '/asset-management/key-audit',
          active: false,
          permission: KEY_AUDIT_PERMISSIONS.READ,
        },
      ],
      svgPath:
        'M8 8V6C8 4.89543 8.89543 4 10 4H14C15.1046 4 16 4.89543 16 6V8M8 8H4C2.89543 8 2 8.89543 2 10V18C2 19.1046 2.89543 20 4 20H20C21.1046 20 22 19.1046 22 18V10C22 8.89543 21.1046 8 20 8H16M8 8H16M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z',
    },
    {
      icon: 'scorecard',
      label: 'Score Card',
      route: '/score-card',
      active: false,
      permission: SCORECARD_PERMISSIONS.READ,
      svgPaths: [
        'M13 20.8268V22H14.1734C14.5827 22 14.7874 22 14.9715 21.9238C15.1555 21.8475 15.3003 21.7028 15.5897 21.4134L20.4133 16.5894C20.6864 16.3164 20.8229 16.1799 20.8959 16.0327C21.0347 15.7525 21.0347 15.4236 20.8959 15.1434C20.8229 14.9961 20.6864 14.8596 20.4133 14.5866C20.1403 14.3136 20.0038 14.1771 19.8565 14.1041C19.5763 13.9653 19.2473 13.9653 18.9671 14.1041C18.8198 14.1771 18.6833 14.3136 18.4103 14.5866L18.4103 14.5866L13.5867 19.4106C13.2972 19.7 13.1525 19.8447 13.0762 20.0287C13 20.2128 13 20.4174 13 20.8268Z',
        'M19 11C19 11 19 9.4306 18.8478 9.06306C18.6955 8.69552 18.4065 8.40649 17.8284 7.82843L13.0919 3.09188C12.593 2.593 12.3436 2.34355 12.0345 2.19575C11.9702 2.165 11.9044 2.13772 11.8372 2.11401C11.5141 2 11.1614 2 10.4558 2C7.21082 2 5.58831 2 4.48933 2.88607C4.26731 3.06508 4.06508 3.26731 3.88607 3.48933C3 4.58831 3 6.21082 3 9.45584V14C3 17.7712 3 19.6569 4.17157 20.8284C5.23467 21.8915 6.8857 21.99 10 21.9991M12 2.5V3C12 5.82843 12 7.24264 12.8787 8.12132C13.7574 9 15.1716 9 18 9H18.5',
      ],
    },
    {
      icon: 'scorecard-builder',
      label: 'Score Card Builder',
      route: null,
      active: false,
      permission: SCORECARD_PARAMETER_PERMISSIONS.READ,
      hasSubmenu: true,
      submenus: [
        {
          label: 'Parameters',
          route: '/score-card-builder/parameters',
          active: false,
          permission: SCORECARD_PARAMETER_PERMISSIONS.READ,
        },
        {
          label: 'Scoring Model',
          route: '/score-card-builder/scoring-model',
          active: false,
          permission: SCORECARD_PARAMETER_PERMISSIONS.READ,
        },
      ],
      svgPath:
        'M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89799 22.1213 3.4376 22.1213 4.00023C22.1213 4.56286 21.8978 5.10246 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z',
    },
    {
      icon: 'settings',
      label: 'System Configuration',
      route: '/system-configuration',
      active: false,
      permission: null,
      svgPaths: [
        'M21.3175 7.14139L20.8239 6.28479C20.4506 5.63696 20.264 5.31305 19.9464 5.18388C19.6288 5.05472 19.2696 5.15664 18.5513 5.36048L17.3311 5.70418C16.8725 5.80994 16.3913 5.74994 15.9726 5.53479L15.6357 5.34042C15.2766 5.11043 15.0004 4.77133 14.8475 4.37274L14.5136 3.37536C14.294 2.71534 14.1842 2.38533 13.9228 2.19657C13.6615 2.00781 13.3143 2.00781 12.6199 2.00781H11.5051C10.8108 2.00781 10.4636 2.00781 10.2022 2.19657C9.94085 2.38533 9.83106 2.71534 9.61149 3.37536L9.27753 4.37274C9.12465 4.77133 8.84845 5.11043 8.48937 5.34042L8.15249 5.53479C7.73374 5.74994 7.25259 5.80994 6.79398 5.70418L5.57375 5.36048C4.85541 5.15664 4.49625 5.05472 4.17867 5.18388C3.86109 5.31305 3.67445 5.63696 3.30115 6.28479L2.80757 7.14139C2.45766 7.74864 2.2827 8.05227 2.31666 8.37549C2.35061 8.69871 2.58483 8.95918 3.05326 9.48012L4.0843 10.6328C4.3363 10.9518 4.51521 11.5078 4.51521 12.0077C4.51521 12.5078 4.33636 13.0636 4.08433 13.3827L3.05326 14.5354C2.58483 15.0564 2.35062 15.3168 2.31666 15.6401C2.2827 15.9633 2.45766 16.2669 2.80757 16.8741L3.30114 17.7307C3.67443 18.3785 3.86109 18.7025 4.17867 18.8316C4.49625 18.9608 4.85542 18.8589 5.57377 18.655L6.79394 18.3113C7.25263 18.2055 7.73387 18.2656 8.15267 18.4808L8.4895 18.6752C8.84851 18.9052 9.12464 19.2442 9.2775 19.6428L9.61149 20.6403C9.83106 21.3003 9.94085 21.6303 10.2022 21.8191C10.4636 22.0078 10.8108 22.0078 11.5051 22.0078H12.6199C13.3143 22.0078 13.6615 22.0078 13.9228 21.8191C14.1842 21.6303 14.294 21.3003 14.5136 20.6403L14.8476 19.6428C15.0004 19.2442 15.2765 18.9052 15.6356 18.6752L15.9724 18.4808C16.3912 18.2656 16.8724 18.2055 17.3311 18.3113L18.5513 18.655C19.2696 18.8589 19.6288 18.9608 19.9464 18.8316C20.264 18.7025 20.4506 18.3785 20.8239 17.7307L21.3175 16.8741C21.6674 16.2669 21.8423 15.9633 21.8084 15.6401C21.7744 15.3168 21.5402 15.0564 21.0718 14.5354L20.0407 13.3827C19.7887 13.0636 19.6098 12.5078 19.6098 12.0077C19.6098 11.5078 19.7888 10.9518 20.0407 10.6328L21.0718 9.48012C21.5402 8.95918 21.7744 8.69871 21.8084 8.37549C21.8423 8.05227 21.6674 7.74864 21.3175 7.14139Z',
        'M15.5195 12C15.5195 13.933 13.9525 15.5 12.0195 15.5C10.0865 15.5 8.51953 13.933 8.51953 12C8.51953 10.067 10.0865 8.5 12.0195 8.5C13.9525 8.5 15.5195 10.067 15.5195 12Z',
      ],
    },
  ];

  // Computed menu items filtered by permissions
  menuItems = computed(() => {
    // Read trigger to make this computed signal depend on active state changes
    this.activeStateTrigger();
    return this.allMenuItems
      .filter((item) => {
        // If no permission required, show item
        if (!item.permission) {
          // For Settings menu, check if user has any settings-related permission
          if (item.label === 'Settings') {
            return this.authService.hasAnyPermission([
              AUDIT_SCHEDULE_PERMISSIONS.READ,
              DYNAMIC_FORM_PERMISSIONS.READ,
            ]);
          }
          // For Masters menu, check if user has any masters-related permission
          if (item.label === 'Masters') {
            return this.authService.hasAnyPermission([
              USER_PERMISSIONS.READ,
              ROLE_PERMISSIONS.READ,
              DEPARTMENT_PERMISSIONS.READ,
              AUDIT_TYPE_PERMISSIONS.READ,
              GENERIC_PERMISSIONS.READ,
              PERMISSION_PERMISSIONS.READ,
            ]);
          }
          if (item.label === 'System Configuration') {
            return this.authService.user()?.userType === UserType.SUPER_ADMIN;
          }
          return true;
        }

        // Check if user has required permission
        return this.authService.hasPermission(item.permission);
      })
      .map((item) => {
        // Filter submenus if item has submenus
        if (item.hasSubmenu && item.submenus) {
          const filteredSubmenus = item.submenus.filter((submenu) => {
            if (!submenu.permission) {
              return true;
            }
            return this.authService.hasPermission(submenu.permission);
          });

          // Only create new object if submenus were filtered, otherwise preserve reference
          if (filteredSubmenus.length !== item.submenus.length) {
            return {
              ...item,
              submenus: filteredSubmenus,
            };
          }
          // Preserve the original item reference so active state updates are reflected
          return item;
        }
        return item;
      })
      .filter((item) => {
        // Hide parent menu if all submenus are filtered out
        if (item.hasSubmenu && item.submenus && item.submenus.length === 0) {
          return false;
        }
        return true;
      });
  });

  constructor(private router: Router, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    // Set initial active state
    this.updateActiveState();

    // Listen to route changes
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateActiveState();
      });
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

  private updateActiveState() {
    const currentUrl = this.router.url;
    const currentUrlPath = currentUrl.split('?')[0]; // Remove query params for comparison

    // Update active state on all menu items (not filtered)
    this.allMenuItems.forEach((item: any) => {
      if (item.hasSubmenu && item.submenus) {
        // Handle submenus
        item.active = false;
        item.submenus.forEach((submenu: any) => {
          const wasSubmenuActive = submenu.active;
          if (submenu.route !== null && submenu.route !== '/') {
            // Check exact match
            if (currentUrlPath === submenu.route) {
              submenu.active = true;
            } else if (currentUrlPath.startsWith(submenu.route + '/')) {
              // Direct child route (e.g., /gold/valuation-tool/child)
              submenu.active = true;
            } else {
              // Special handling for "Loan Process Checking" submenu: only active
              // when current URL is exactly this route or a direct child of it
              // (e.g. /surveillance/loan-process-checking/this-week), not when
              // on sibling routes like /surveillance/routine-checking
              if (submenu.label === 'Loan Process Checking') {
                submenu.active =
                  currentUrlPath === submenu.route ||
                  currentUrlPath.startsWith(submenu.route + '/');
              } else {
                // Check if current URL is a child route where the submenu route segment
                // is a prefix of a segment in the current URL
                // (e.g., 'valuation-tool' matches 'valuation-tool-audit-process')
                const submenuSegments = submenu.route
                  .split('/')
                  .filter((s: string) => s.length > 0);
                const currentSegments = currentUrlPath
                  .split('/')
                  .filter((s: string) => s.length > 0);

                // Check if current URL starts with submenu route segments
                if (currentSegments.length >= submenuSegments.length) {
                  let matches = true;
                  for (let i = 0; i < submenuSegments.length; i++) {
                    const submenuSegment = submenuSegments[i];
                    const currentSegment = currentSegments[i];

                    // Exact match or current segment starts with submenu segment
                    // (to handle cases like 'valuation-tool' matching 'valuation-tool-audit-process')
                    if (
                      currentSegment !== submenuSegment &&
                      !currentSegment.startsWith(submenuSegment + '-')
                    ) {
                      matches = false;
                      break;
                    }
                  }
                  submenu.active = matches;
                } else {
                  submenu.active = false;
                }
              }
            }
          } else {
            submenu.active = false;
          }

          if (submenu.active) {
            item.active = true;
            // Auto-expand menu if submenu is active
            this.expandedMenu = item.label;
            // Emit title change when a submenu becomes active
            if (!wasSubmenuActive) {
              this.activeMenuTitle.emit(submenu.label);
            }
          }
        });
      } else {
        // Handle regular menu items
        const wasActive = item.active;
        // Use route + '/' for startsWith so /score-card does not match /score-card-builder
        item.active =
          item.route !== null &&
          (currentUrlPath === item.route ||
            (item.route !== '/dashboard' &&
              currentUrlPath.startsWith(item.route + '/')));

        // Emit title change when an item becomes active
        if (!wasActive && item.active) {
          this.activeMenuTitle.emit(item.label);
        }
      }
    });

    // Trigger computed signal recalculation
    this.activeStateTrigger.update((v) => v + 1);

    // Mark for check to ensure change detection runs with OnPush strategy
    this.cdr.markForCheck();
  }

  toggleSubmenu(menuLabel: string) {
    // Collapse other menus and toggle current menu
    if (this.expandedMenu === menuLabel) {
      this.expandedMenu = null;
    } else {
      this.expandedMenu = menuLabel;
    }
  }

  isSubmenuExpanded(menuLabel: string): boolean {
    return this.expandedMenu === menuLabel;
  }

  onToggleSidebar() {
    if (!this.isMobile) {
      this.toggleSidebar.emit(!this.collapsed);
    }
  }

  onMobileSidebarClose() {
    this.mobileSidebarClose.emit();
  }

  onNavigationClick() {
    // Close sidebar on mobile/tablet when navigation item is clicked
    if (this.isMobile && this.sidebarOpen) {
      this.onMobileSidebarClose();
    }
  }

  onMenuHover(menuLabel: string | null, event?: MouseEvent) {
    // Close any open tooltip from regular menu items immediately
    if (this.hoveredRegularMenu) {
      this.hoveredRegularMenu = null;
    }
    // Clear any pending hide timeout for tooltip
    if (this.regularMenuHideTimeout) {
      clearTimeout(this.regularMenuHideTimeout);
      this.regularMenuHideTimeout = null;
    }

    // Clear any pending hide timeout for popover
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    // Only show hover popover on desktop when collapsed
    if (!this.isMobile && this.collapsed && menuLabel && event) {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      this.menuItemPositions.set(menuLabel, {
        top: rect.top,
        left: rect.left + rect.width,
      });
      // Switch to the new menu item immediately
      this.hoveredMenu = menuLabel;
    } else if (!this.isMobile && this.collapsed && menuLabel) {
      this.hoveredMenu = menuLabel;
    }
  }

  onRegularMenuHover(menuLabel: string, event?: MouseEvent) {
    // Close any open popover from submenu items immediately
    if (this.hoveredMenu) {
      this.hoveredMenu = null;
    }
    // Clear any pending hide timeout for popover
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    // Clear any pending hide timeout for tooltip
    if (this.regularMenuHideTimeout) {
      clearTimeout(this.regularMenuHideTimeout);
      this.regularMenuHideTimeout = null;
    }

    // Only show tooltip on desktop when collapsed
    if (!this.isMobile && this.collapsed && menuLabel && event) {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      this.regularMenuItemPositions.set(menuLabel, {
        top: rect.top + rect.height / 2,
        left: rect.left + rect.width,
      });
      this.hoveredRegularMenu = menuLabel;
    } else if (!this.isMobile && this.collapsed && menuLabel) {
      this.hoveredRegularMenu = menuLabel;
    }
  }

  onRegularMenuLeave() {
    // Clear any pending hide timeout
    if (this.regularMenuHideTimeout) {
      clearTimeout(this.regularMenuHideTimeout);
      this.regularMenuHideTimeout = null;
    }
    // Hide tooltip immediately when mouse leaves (no delay needed for regular menu items)
    this.hoveredRegularMenu = null;
  }

  isRegularMenuHovered(menuLabel: string): boolean {
    return this.hoveredRegularMenu === menuLabel;
  }

  getTooltipLeft(): number {
    if (this.collapsed && !this.isMobile) {
      // Sidebar width when collapsed (64px = w-16) + gap (8px)
      return 64 + 8;
    }
    return 0;
  }

  getTooltipTop(menuLabel: string): number {
    const position = this.regularMenuItemPositions.get(menuLabel);
    if (position) {
      // Center the tooltip vertically with the menu item
      return position.top;
    }
    return 0;
  }

  onMenuLeave() {
    // Add a delay before hiding to allow mouse to move to popover
    this.hideTimeout = setTimeout(() => {
      this.hoveredMenu = null;
      this.hideTimeout = null;
    }, 200); // 200ms delay for easier mouse movement
  }

  onPopoverEnter() {
    // Clear hide timeout when entering popover to keep it visible
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    // Ensure the popover stays visible - don't change hoveredMenu
  }

  onPopoverLeave() {
    // Hide popover when leaving it
    this.hoveredMenu = null;
  }

  isMenuHovered(menuLabel: string): boolean {
    return this.hoveredMenu === menuLabel;
  }

  getPopoverLeft(): number {
    if (this.hoveredMenu) {
      const position = this.menuItemPositions.get(this.hoveredMenu);
      if (position) {
        return position.left + 4; // Reduced gap of 4px for easier mouse movement
      }
    }
    if (this.collapsed && !this.isMobile) {
      // Fallback: Sidebar width when collapsed (64px = w-16) + gap (4px)
      return 64 + 4;
    }
    return 0;
  }

  getPopoverTop(menuLabel: string): number {
    const position = this.menuItemPositions.get(menuLabel);
    if (position) {
      return position.top;
    }
    return 0;
  }
}
